// React
import { useState } from 'react';

// refine and mantine
import {
  Box,
  Flex,
  MultiSelect,
  Select,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';

import { MRT_ColumnDef } from 'mantine-react-table';

// resource

// interfaces
import { Organization, Permission, Role } from 'interfaces';

// Custom
import { DataBadge } from 'components/common/DataBadge';

const toTitleCase = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatPermissionLabel = (permission: Permission) =>
  `${toTitleCase(permission.resource)} — ${toTitleCase(permission.action)}`;

export const RoleColumns = (
  organizationProp: Organization[],
  permissionProp: Permission[],
  isAdmin: boolean
) => {
  const columns: MRT_ColumnDef<Role>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      accessorFn: (row) => row.name,
      Edit: ({ cell }) => {
        const [value, setValue] = useState<string>(cell.getValue() as string);
        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          setValue(event.target.value);
          cell.row._valuesCache.name = event.target.value;
        };
        return (
          <TextInput
            type="text"
            value={value}
            onChange={handleChange}
            placeholder="Name"
            data-testid="roleNameTextInput"
          />
        );
      },
    },
    {
      accessorKey: 'permissions',
      header: 'Permissions',
      Edit: ({ cell }) => {
        const [selectedValues, setSelectedValues] = useState<Permission[]>(
          (cell.getValue() as Permission[]) || []
        );

        const handlePermissionChange = (values: string[]) => {
          const updatedPermissions = permissionProp.filter((perm) =>
            values.includes(perm.id)
          );
          setSelectedValues(updatedPermissions);
          // Update the cell's data cache
          cell.row._valuesCache.permissions = updatedPermissions;
        };

        return (
          <MultiSelect
            label="Permissions"
            data={permissionProp.map((perm) => ({
              value: perm.id,
              label: formatPermissionLabel(perm),
            }))}
            value={selectedValues.map((perm) => perm.id)}
            onChange={handlePermissionChange}
            placeholder="Select permissions"
            data-testid="permissionsSelect"
          />
        );
      },
      Cell: ({ cell }) => {
        const selectedPermissions = (cell.getValue() as Permission[]) || [];
        const permissionCount = selectedPermissions.length;
        const label = permissionCount
          ? selectedPermissions.map(formatPermissionLabel).join(', ')
          : 'No permissions assigned';
        return (
          <Tooltip
            label={label}
            withinPortal
            position="top"
            withArrow
            offset={4}
            openDelay={150}
            multiline
            w={300}
            styles={{ tooltip: { whiteSpace: 'pre-wrap' } }}
          >
            <Text
              size="sm"
              c="dimmed"
              style={{ display: 'inline-flex', alignItems: 'center' }}
              data-testid="permissionsCellText"
            >
              {permissionCount} permissions
            </Text>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'organizationID',
      header: 'Organization',
      Edit: ({ cell }) => {
        const [selectedValue, setSelectedValue] = useState<string>(
          cell.getValue() as string
        );
        const handleOrganizationChange = (value: string) => {
          setSelectedValue(value);
          cell.row._valuesCache.organizationID = value;
        };
        const component = isAdmin ? (
          <Select
            label="Organization"
            data={organizationProp.map((org) => ({
              value: org.id,
              label: org.name,
            }))}
            key={selectedValue}
            value={selectedValue}
            onChange={handleOrganizationChange}
            data-testid="organizationSelect"
            errorProps={{ 'data-testid': 'role-organization-error' }}
            placeholder="Select organizations"
          />
        ) : (
          <Box w={'100%'}>
            <Flex
              justify={'start'}
              align={'start'}
              gap={'xs'}
              direction={'row'}
            >
              <Text size="sm">Organization</Text>
              <DataBadge>{organizationProp[0]?.name}</DataBadge>
            </Flex>
          </Box>
        );

        return component;
      },

      Cell: ({ cell }) => {
        const selectedOrganizations = [cell.getValue()] as string[] | null;
        const organizations = organizationProp.filter((org) =>
          selectedOrganizations.includes(org.id)
        )[0];

        const badges = [];
        if (isAdmin) {
          if (organizations) {
            badges.push(
              <DataBadge key={organizations.id}>
                {organizations?.name}
              </DataBadge>
            );
          } else {
            badges.push(
              <DataBadge key={'validKey'}>Site Wide Organization</DataBadge>
            );
          }
        } else {
          badges.push(
            <DataBadge key={organizations.id}>{organizations?.name}</DataBadge>
          );
        }
        return badges;
      },
    },
  ];
  return columns;
};
